import type { VisionConfig } from "@studystraight/shared-config";
import type { VisionResult } from "@studystraight/shared-types";
import type { VisionProvider } from "@studystraight/vision-core";

export interface PerfSnapshot {
  /** FPS mục tiêu hiện tại (có thể đã bị tự hạ). */
  targetFps: number;
  /** FPS thực đo được trong ~1 giây gần nhất. */
  actualFps: number;
  inferenceP50Ms: number;
  inferenceP95Ms: number;
  /** Số frame bị bỏ vì provider trả null (không thấy người / timestamp trùng). */
  droppedFrames: number;
  degraded: boolean;
}

export interface InferenceLoopOptions {
  provider: VisionProvider;
  config: VisionConfig;
  getVideo: () => HTMLVideoElement | null;
  /** `inferenceMs` đi kèm frame để Debug Mode (Bước 7) không phải đo lại lần nữa. */
  onResult: (result: VisionResult, inferenceMs: number) => void;
  onPerf?: (perf: PerfSnapshot) => void;
  onError?: (err: unknown) => void;
}

export interface InferenceLoop {
  start: () => void;
  stop: () => void;
  readonly perf: PerfSnapshot;
}

function percentile(sorted: readonly number[], p: number): number {
  if (sorted.length === 0) return 0;
  const idx = Math.min(sorted.length - 1, Math.floor((sorted.length - 1) * p));
  return sorted[idx] ?? 0;
}

/**
 * Vòng lặp inference — Prompt.md §31.
 *
 * Ba việc: throttle về `targetInferenceFps`, đo `inferenceMs`, và **tự hạ FPS** khi p95 vượt
 * `degradeAboveP95Ms`.
 *
 * Adaptive FPS thay vì hằng số: dải thiết bị của trẻ rất rộng (iPhone SE cũ → flagship Android).
 * Tự hạ FPS giữ được responsiveness trên máy yếu mà không cần whitelist thiết bị, và đồng thời
 * là dữ liệu verify DU #1 (có cần Web Worker hay không).
 */
export function createInferenceLoop(options: InferenceLoopOptions): InferenceLoop {
  const { provider, config, getVideo, onResult, onPerf, onError } = options;

  let rafId: number | null = null;
  let running = false;
  let inFlight = false;

  let targetFps = config.targetInferenceFps;
  let lastFrameAt = 0;
  let droppedFrames = 0;
  let degraded = false;
  /** Mốc `performance.now()` lúc `start()` — xem ghi chú về timestamp trong `tick()`. */
  let loopStartedAt = 0;

  const inferenceSamples: number[] = [];
  let framesInWindow = 0;
  let windowStartedAt = 0;
  let actualFps = 0;

  const perf: PerfSnapshot = {
    targetFps,
    actualFps: 0,
    inferenceP50Ms: 0,
    inferenceP95Ms: 0,
    droppedFrames: 0,
    degraded: false,
  };

  function pushSample(ms: number): void {
    inferenceSamples.push(ms);
    if (inferenceSamples.length > config.perfSampleWindow) inferenceSamples.shift();
  }

  function updatePerf(nowMs: number): void {
    framesInWindow += 1;
    if (windowStartedAt === 0) windowStartedAt = nowMs;
    const elapsed = nowMs - windowStartedAt;
    if (elapsed >= 1000) {
      actualFps = (framesInWindow * 1000) / elapsed;
      framesInWindow = 0;
      windowStartedAt = nowMs;
    }

    const sorted = [...inferenceSamples].sort((a, b) => a - b);
    const p95 = percentile(sorted, 0.95);

    // Chỉ hạ FPS khi đã có đủ mẫu: vài frame đầu luôn chậm (warm-up của WASM/GPU shader) và
    // sẽ kéo cả phiên xuống FPS sàn nếu tin ngay.
    if (inferenceSamples.length >= config.perfSampleWindow / 2) {
      if (p95 > config.degradeAboveP95Ms && targetFps > config.minInferenceFps) {
        targetFps = Math.max(config.minInferenceFps, targetFps - 2);
        degraded = true;
      }
    }

    perf.targetFps = targetFps;
    perf.actualFps = actualFps;
    perf.inferenceP50Ms = percentile(sorted, 0.5);
    perf.inferenceP95Ms = p95;
    perf.droppedFrames = droppedFrames;
    perf.degraded = degraded;
    onPerf?.({ ...perf });
  }

  function tick(): void {
    if (!running) return;
    rafId = requestAnimationFrame(tick);

    const nowMs = performance.now();
    const minInterval = 1000 / targetFps;
    if (nowMs - lastFrameAt < minInterval) return;

    // `inFlight` là chốt chặn quan trọng nhất: detect() chậm hơn khoảng throttle trên máy yếu,
    // không có chốt này queue sẽ dài vô hạn và app đông cứng.
    if (inFlight) return;

    const video = getVideo();
    if (!video) return;

    // Chưa có frame nào giải mã xong (`HAVE_CURRENT_DATA`) thì `detectForVideo` không có gì để
    // đọc. Không tính là dropped frame: đây là warm-up bình thường của <video>, không phải
    // "không thấy người".
    if (video.readyState < 2 || video.videoWidth === 0) return;

    lastFrameAt = nowMs;
    inFlight = true;
    provider
      .detect({
        kind: "video",
        element: video,
        /*
         * Đồng hồ đơn điệu tính từ lúc start(), KHÔNG dùng `video.currentTime`.
         *
         * Với <video> gắn MediaStream, iOS Safari giữ `currentTime` ở 0 (hoặc nhảy rất thô) —
         * mà `MediaPipeWebVisionProvider.detect()` bỏ mọi frame có `timestampMs <= lastTimestampMs`.
         * Hệ quả trên iPhone: camera vẫn hiện, nhưng KHÔNG frame nào được nhận diện. Chỉ cần một
         * đồng hồ tăng đều là đủ cho MediaPipe nội suy tracking, và nó vẫn bắt đầu gần 0 giống
         * media time nên `MonitoringController` (trục media) không đổi hành vi.
         */
        timestampMs: nowMs - loopStartedAt,
      })
      .then((result) => {
        const finishedAt = performance.now();
        // Đo trọn vòng detect() (kể cả overhead của adapter), không chỉ thời gian trong WASM:
        // đây mới là con số quyết định vòng lặp có kịp giữ FPS mục tiêu hay không.
        const inferenceMs = finishedAt - nowMs;
        if (result) onResult(result, inferenceMs);
        else droppedFrames += 1;
        pushSample(inferenceMs);
        updatePerf(finishedAt);
      })
      .catch((err: unknown) => onError?.(err))
      .finally(() => {
        inFlight = false;
      });
  }

  return {
    start() {
      if (running) return;
      running = true;
      lastFrameAt = 0;
      windowStartedAt = 0;
      loopStartedAt = performance.now();
      rafId = requestAnimationFrame(tick);
    },
    stop() {
      running = false;
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = null;
    },
    get perf() {
      return { ...perf };
    },
  };
}
